import { useEffect, useState } from "react";
import { Inbox, Mail, Flag, AlertTriangle, CheckCircle2, Clock, ListTodo, Timer, TrendingUp } from "lucide-react";
import { api, errText } from "../../lib/api";
import { toast } from "sonner";
import { card, timeAgo, ROLE_LABEL } from "./common";

const Stat = ({ icon: Icon, label, value, accent, onClick }) => (
  <button type="button" onClick={onClick} className={`${card} p-4 text-left hover:border-accent/50 transition-colors`} data-testid={`panel-stat-${label}`}>
    <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${accent ? "bg-accent text-white" : "bg-secondary text-foreground/60"}`}><Icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} /></span>
    <p className="text-2xl font-extrabold mt-2.5">{value ?? "—"}</p>
    <p className="text-xs text-muted-foreground font-semibold">{label}</p>
  </button>
);

export default function PanelOverview({ go }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/panel/overview").then(({ data }) => setData(data)).catch((e) => toast.error(errText(e)));
  }, []);
  if (!data) return <div className="h-60 bg-muted rounded-2xl animate-pulse" />;
  const s = data.stats;
  return (
    <div className="space-y-5" data-testid="panel-overview">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <Stat icon={Inbox} label="Offene Tickets" value={s.open_tickets} accent onClick={() => go("tickets")} />
        <Stat icon={AlertTriangle} label="SLA überfällig" value={s.sla_overdue} onClick={() => go("tickets")} />
        <Stat icon={AlertTriangle} label="Hohe Priorität" value={s.high_priority} onClick={() => go("tickets")} />
        <Stat icon={Mail} label="Ungelesene E-Mails" value={s.unread_emails} onClick={() => go("emails")} />
        <Stat icon={Flag} label="Offene Meldungen" value={s.open_reports} onClick={() => go("reports")} />
        <Stat icon={ListTodo} label="Offene Aufgaben" value={s.open_tasks} onClick={() => go("tasks")} />
        <Stat icon={Clock} label="Wartet auf Kunde" value={s.waiting_customer} onClick={() => go("tickets")} />
        <Stat icon={TrendingUp} label="Neu heute" value={s.new_today} onClick={() => go("tickets")} />
        <Stat icon={CheckCircle2} label="Heute erledigt" value={s.resolved_today} onClick={() => go("tickets")} />
        <Stat icon={Timer} label="Ø Erstantwort" value={s.avg_response_min != null ? `${s.avg_response_min} Min.` : "—"} onClick={() => go("tickets")} />
        <Stat icon={Inbox} label="Tickets (30 Tage)" value={s.tickets_30d} onClick={() => go("tickets")} />
      </div>
      {!data.email_configured && (
        <p className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-xl px-4 py-3">
          Hinweis: Es ist kein E-Mail-Posteingang (IMAP) konfiguriert – eingehende Mails werden nicht abgerufen. Versand funktioniert über SMTP.
        </p>
      )}
      <div className="grid lg:grid-cols-2 gap-5">
        <div className={`${card} p-5`}>
          <p className="font-bold text-sm mb-3">Team-Auslastung</p>
          <div className="space-y-2" data-testid="panel-team-load">
            {(data.per_employee || []).map((e) => (
              <div key={e.id} className="flex items-center gap-3 text-sm">
                <span className="font-semibold flex-1 truncate">{e.name || e.username} <span className="text-[11px] text-muted-foreground">({ROLE_LABEL[e.role] || e.role}{e.status !== "ACTIVE" ? " · deaktiviert" : ""})</span></span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">{e.open_tickets} Tickets · {e.open_tasks} Aufgaben</span>
              </div>
            ))}
          </div>
        </div>
        <div className={`${card} p-5`}>
          <p className="font-bold text-sm mb-3">Letzte Team-Aktivität</p>
          <div className="space-y-2.5" data-testid="panel-recent-activity">
            {(data.recent_activity || []).length === 0 && <p className="text-sm text-muted-foreground">Noch keine Aktivität.</p>}
            {(data.recent_activity || []).map((r) => (
              <div key={r.id} className="text-sm flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-accent mt-1.5 shrink-0" />
                <span className="flex-1 min-w-0">
                  <b>{r.author_name}</b> {r.internal ? "notierte intern zu" : "antwortete auf"} <span className="font-mono text-xs">{r.ticket?.ticket_id}</span>
                  <span className="block text-xs text-muted-foreground truncate">{r.ticket?.subject} · {timeAgo(r.created_at)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
